import Util from '../utils/util';

import { Opcodes } from '@kaetram/common/network';

import type Ability from '../entity/character/player/ability';
import type { SelectCallback } from './profile/impl/abilities';

interface SlotData {
    type: 'ability' | 'item';
    key: string;
    inventoryIndex?: number;
    count?: number;
}

interface QuickSlotElement extends HTMLElement {
    slotData?: SlotData;
}

export default class QuickSlots {
    private bar: HTMLElement = document.querySelector('#ability-shortcut')!;
    private slots: QuickSlotElement[] = [];

    private cooldownTimers = new Map<string, { overlay: HTMLElement; interval: number }>();

    private abilities?: { [key: string]: Ability };

    private abilityCallback?: SelectCallback;
    private itemUseCallback?: (index: number) => void;
    private itemClearCallback?: (slot: number) => void;

    public constructor() {
        for (let i = 0; i < this.bar.children.length; i++) {
            let el = this.bar.children[i] as QuickSlotElement;

            this.slots.push(el);

            el.addEventListener('click', () => this.use(i));

            el.addEventListener('contextmenu', (e: MouseEvent) => {
                e.preventDefault();
                this.clearSlot(i);
            });

            el.addEventListener('dragover', (e: DragEvent) => {
                e.preventDefault();
                el.classList.add('active');
            });

            el.addEventListener('dragleave', () => el.classList.remove('active'));

            el.addEventListener('drop', (e: DragEvent) => {
                e.preventDefault();
                el.classList.remove('active');

                let abilityKey = e.dataTransfer?.getData('ability-key');

                if (abilityKey) {
                    this.assignAbility(i, abilityKey);
                    this.abilityCallback?.(Opcodes.Ability.QuickSlot, abilityKey, i);
                }
            });

            let label = document.createElement('span');
            label.classList.add('quickslot-key-label');
            label.textContent = `${i + 1}`;

            let countEl = document.createElement('span');
            countEl.classList.add('quickslot-count');

            el.append(label, countEl);
        }
    }

    public assignAbility(index: number, key: string): void {
        this.clearSlot(index);
        this.cleanDuplicates(key);

        let slot = this.slots[index];
        slot.slotData = { type: 'ability', key };

        slot.classList.add(`quickslot-icon-${key}`);
    }

    public assignItem(
        quickslotIndex: number,
        inventoryIndex: number,
        key: string,
        count = 1
    ): void {
        this.clearSlot(quickslotIndex);

        let slot = this.slots[quickslotIndex];
        slot.slotData = { type: 'item', key, inventoryIndex, count };
        slot.classList.add('has-item');
        slot.style.backgroundImage = Util.getImageURL(key);

        this.updateCount(slot, count);
    }

    public use(index: number): void {
        let slot = this.slots[index];

        if (!slot?.slotData?.key) return;

        let { type, key, inventoryIndex } = slot.slotData;

        if (type === 'ability') this.abilityCallback?.(Opcodes.Ability.Use, key);
        else if (type === 'item' && inventoryIndex !== undefined)
            this.itemUseCallback?.(inventoryIndex);
    }

    public handleAbilityUpdate(key: string, _level: number, quickSlot: number): void {
        if (quickSlot === -1) return;

        this.assignAbility(quickSlot, key);
    }

    public syncInventorySlot(inventoryIndex: number, key: string, count: number): void {
        for (let i = 0; i < this.slots.length; i++) {
            let data = this.slots[i].slotData;

            if (!data || data.type !== 'item' || data.inventoryIndex !== inventoryIndex) continue;

            if (!key || count <= 0) {
                this.slots[i].classList.remove('has-item');
                this.slots[i].style.backgroundImage = '';
                data.key = '';
                data.count = 0;
                this.updateCount(this.slots[i], 0);
                continue;
            }

            if (key !== data.key || !data.key) {
                this.assignItem(i, inventoryIndex, key, count);
                continue;
            }

            data.count = count;
            this.updateCount(this.slots[i], count);
        }
    }

    private updateCount(slot: QuickSlotElement, count: number): void {
        let countEl = slot.querySelector('.quickslot-count')!;

        if (!countEl) return;

        countEl.textContent = count > 1 ? Util.getCount(count) : '';
    }

    private clearSlot(index: number): void {
        let slot = this.slots[index];
        if (!slot) return;

        let wasItem = slot.slotData?.type === 'item';

        if (slot.slotData?.type === 'ability')
            slot.classList.remove(`quickslot-icon-${slot.slotData.key}`);

        slot.classList.remove('has-item');
        slot.style.backgroundImage = '';
        slot.slotData = undefined;
        this.updateCount(slot, 0);

        if (wasItem) this.itemClearCallback?.(index);
    }

    private cleanDuplicates(key: string): void {
        for (let i = 0; i < this.slots.length; i++) {
            let data = this.slots[i].slotData;
            if (data?.type === 'ability' && data.key === key) this.clearSlot(i);
        }
    }

    public setAbilities(abilities: { [key: string]: Ability }): void {
        this.abilities = abilities;
    }

    public syncCooldown(key: string): void {
        if (!this.abilities) return;

        let ability = this.abilities[key];

        if (!ability) return;

        for (let i = 0; i < this.slots.length; i++) {
            let data = this.slots[i].slotData;

            if (!data || data.type !== 'ability' || data.key !== key) continue;

            if (ability.isOnCooldown()) this.addCooldownOverlay(this.slots[i], key, ability);
            else this.removeCooldownOverlay(key);
        }
    }

    private addCooldownOverlay(element: HTMLElement, key: string, ability: Ability): void {
        this.removeCooldownOverlay(key);

        let overlay = document.createElement('div');
        overlay.classList.add('quickslot-cooldown');

        let text = document.createElement('span');
        text.classList.add('cooldown-text');
        overlay.append(text);

        element.append(overlay);

        this.updateCooldownText(text, ability);

        let interval = window.setInterval(() => {
            if (!ability.isOnCooldown()) {
                this.removeCooldownOverlay(key);
                return;
            }

            this.updateCooldownText(text, ability);
        }, 250);

        this.cooldownTimers.set(key, { overlay, interval });
    }

    private removeCooldownOverlay(key: string): void {
        let timer = this.cooldownTimers.get(key);

        if (!timer) return;

        clearInterval(timer.interval);
        timer.overlay.remove();
        this.cooldownTimers.delete(key);
    }

    private updateCooldownText(element: HTMLElement, ability: Ability): void {
        let remaining = Math.ceil(ability.getCooldownRemaining() / 1000);

        if (remaining >= 60) {
            let mins = Math.floor(remaining / 60),
                secs = remaining % 60;
            element.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
        } else element.textContent = `${remaining}s`;
    }

    public onAbility(callback: SelectCallback): void {
        this.abilityCallback = callback;
    }

    public onItemUse(callback: (index: number) => void): void {
        this.itemUseCallback = callback;
    }

    public onItemClear(callback: (slot: number) => void): void {
        this.itemClearCallback = callback;
    }
}
